/*
 * Shipping Protection: keeps exactly one protection line in the cart, priced
 * from the subtotal of everything else and the shopper's country.
 *
 * - Off by default. The toggle writes the cart attribute "Shipping Protection"
 *   ("Added" or "Declined"); only an "Added" cart gets protection.
 * - Removed automatically when it is the only thing left in the cart.
 * - Re-syncs after any cart change the theme makes (it watches /cart/* fetches),
 *   then fires the theme's own 'cart:refresh' so the drawer and cart page redraw.
 * Tiers (one product variant each) come from snippets/shipping-protection-config.
 */
(() => {
  if (window.ShippingProtection) return;

  const OWN = 'X-Shipping-Protection';
  let syncing = false;
  let pending = false;
  let timer = null;

  const config = () => {
    const el = document.getElementById('ShippingProtectionConfig');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  };

  const request = (url, body) => fetch(url, {
    method: body ? 'POST' : 'GET',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', [OWN]: '1' },
    body: body ? JSON.stringify(body) : undefined
  }).then((r) => r.json());

  // The tier variant for a subtotal in cents: the first tier the subtotal is under.
  const variantFor = (subtotal, tiers, variants) => {
    const tier = tiers.find((t) => t.under === null || subtotal < t.under) || tiers[tiers.length - 1];
    return variants.find((v) => v.title === tier.variant && v.available) || null;
  };

  const refreshTheme = () => {
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  };

  async function sync() {
    const cfg = config();
    if (!cfg) return;
    if (syncing) { pending = true; return; }
    syncing = true;
    try {
      const cart = await request('/cart.js');
      const mine = cart.items.filter((i) => i.handle === cfg.handle);
      const others = cart.items.filter((i) => i.handle !== cfg.handle);
      const added = (cart.attributes || {})['Shipping Protection'] === 'Added';

      let want = null;
      if (others.length && added) {
        const subtotal = others.reduce((sum, i) => sum + i.final_line_price, 0);
        const tiers = cfg.country === 'US' ? cfg.rules.domestic : cfg.rules.international;
        want = variantFor(subtotal, tiers, cfg.variants);
      }

      const correct = want
        ? mine.length === 1 && mine[0].variant_id === want.id && mine[0].quantity === 1
        : mine.length === 0;
      if (correct) return;

      if (mine.length) {
        const updates = {};
        mine.forEach((i) => { updates[i.key] = 0; });
        await request('/cart/update.js', { updates });
      }
      if (want) {
        await request('/cart/add.js', { items: [{ id: want.id, quantity: 1 }] });
      }
      refreshTheme();
    } catch (e) {
      // Never block the cart over protection; the next cart change retries.
    } finally {
      syncing = false;
      if (pending) { pending = false; schedule(); }
    }
  }

  const schedule = () => { clearTimeout(timer); timer = setTimeout(sync, 350); };

  async function setChoice(on) {
    document.querySelectorAll('[data-shipping-protection]').forEach((el) => el.classList.add('is-busy'));
    await request('/cart/update.js', { attributes: { 'Shipping Protection': on ? 'Added' : 'Declined' } });
    await sync();
    refreshTheme();
  }

  // Watch the theme's own cart requests (add, change, update, clear).
  const nativeFetch = window.fetch;
  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    const headers = (init && init.headers) || {};
    const own = headers instanceof Headers ? headers.has(OWN) : Boolean(headers[OWN]);
    const result = nativeFetch.apply(this, arguments);
    if (!own && /\/cart\/(add|change|update|clear)/.test(url)) {
      result.then(() => schedule()).catch(() => {});
    }
    return result;
  };

  document.addEventListener('change', (event) => {
    const box = event.target.closest && event.target.closest('[data-shipping-protection-toggle]');
    if (!box) return;
    event.stopPropagation();
    setChoice(box.checked);
  }, true);

  window.ShippingProtection = { sync, setChoice };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', schedule);
  else schedule();
})();
